import { vec2 } from '@tlaukkan/tsm';
import * as PIXI from 'pixi.js';
import { Background, Resolution } from '../../../shared/constants';
import { Debug } from '../../../shared/debug';
import { BackgroundFluid } from './backgroundFluid';
import { BackgroundLayer } from './backgroundLayer';
import { CiliaLayer } from './ciliaLayer';
import { DeferredRenderPass } from './postprocess/deferredLightingFilter';
import { DepthOfField } from './postprocess/depthOfFieldFilter';
import { MembraneFilter } from './postprocess/membraneFilter';
import { SpriteFactory } from "./spriteFactory";
import { VignetteEffect } from './vignetteEffect';
import { ParticleFactory } from './particleFactory';
import * as particles from 'pixi-particles';

/**Rendering core, shared by all rendering related systems and must be initialized first. */
export class RenderEngine {
	pixiApp: PIXI.Application;
	lighting: PIXI.Container;
	sceneRoot: PIXI.Container;
	depthRoot: PIXI.Container;
	membraneRoot: PIXI.Container;
	vignette: VignetteEffect;
	spriteFactory: SpriteFactory
	inSceneUI: PIXI.Container;

	graphicsUnderlay: PIXI.Graphics;
	graphicsOverlay: PIXI.Graphics;
	graphicsMembrane: PIXI.Graphics;
	renderer: PIXI.Renderer;
	ready: Promise<void>;

	background: BackgroundFluid;
	cilia: BackgroundLayer;

	membraneBuffer: PIXI.RenderTexture;
	depthBuffer: PIXI.RenderTexture;

	lightSource = new vec2([Resolution.Width * (1 / 2), -Resolution.Height]);
	lightSourceOffsetX = -1;

	//Filters
	deferredLighting!: DeferredRenderPass;
	membranes!: MembraneFilter;
	depthOfField!: DepthOfField;

	//Particles
	particleFactory: ParticleFactory;
	particleContainer: particles.LinkedListContainer;
	trailParticleContainer: particles.LinkedListContainer;

	constructor(canvas: HTMLCanvasElement, uiRegion: HTMLElement) {

		this.pixiApp = new PIXI.Application({ view: canvas, width: Resolution.Width, height: Resolution.Height, backgroundColor: 0x000000, resizeTo: uiRegion });

		this.lighting = new PIXI.Container();
		this.lighting.width = this.pixiApp.stage.width;
		this.lighting.height = this.pixiApp.stage.height;

		this.sceneRoot = new PIXI.Container();
		this.sceneRoot.width = this.pixiApp.stage.width;
		this.sceneRoot.height = this.pixiApp.stage.height;

		this.depthRoot = new PIXI.Container();
		this.depthRoot.width = this.pixiApp.stage.width;
		this.depthRoot.height = this.pixiApp.stage.height;

		this.membraneRoot = new PIXI.Container();
		this.membraneRoot.width = this.pixiApp.stage.width;
		this.membraneRoot.height = this.pixiApp.stage.height;

		this.renderer = this.pixiApp.renderer;

		this.vignette = new VignetteEffect(this.pixiApp.stage);
		this.graphicsOverlay = new PIXI.Graphics();
		this.graphicsUnderlay = new PIXI.Graphics();
		this.graphicsMembrane = new PIXI.Graphics();

		this.inSceneUI = new PIXI.Container();

		this.spriteFactory = new SpriteFactory(this.pixiApp);
		this.ready = this.spriteFactory.ready;

		this.background = new BackgroundFluid(this, this.sceneRoot, this.renderer);
		this.cilia = new CiliaLayer(this, this.sceneRoot, this.renderer);

		this.depthBuffer = PIXI.RenderTexture.create({ width: Resolution.Width, height: Resolution.Height });
		this.membraneBuffer = PIXI.RenderTexture.create({ width: Resolution.Width, height: Resolution.Height });

		this.depthRoot.addChild(this.background.mesh);

		this.membraneRoot.addChild(this.cilia.mesh);
		this.membraneRoot.addChild(this.graphicsMembrane);

		this.pixiApp.stage.addChild(this.lighting);
		this.lighting.addChild(this.graphicsUnderlay); //TODO: Not ideal, will get blurred. May need to also render depth for this //follow up, just not using it anywhere now 
		this.lighting.addChild(this.sceneRoot);
		this.pixiApp.stage.addChild(this.graphicsOverlay);
		this.pixiApp.stage.addChild(this.inSceneUI);

		this.lighting.filterArea = new PIXI.Rectangle(0, 0, Resolution.Width, Resolution.Height); //fucks with deferred render

		//Particles
		this.particleContainer = new particles.LinkedListContainer();
		this.trailParticleContainer = new particles.LinkedListContainer();
		this.particleFactory = new ParticleFactory(this.particleContainer);
		this.particleFactory.loadAssets();
		this.pixiApp.stage.addChild(this.particleContainer);
		this.trailParticleContainer.scale = new PIXI.Point(Background.TrailsTextureScaleFactor, Background.TrailsTextureScaleFactor);
		this.background.imprintContainer.addChild(this.trailParticleContainer);

		if (!Debug.CaptureMode) {
			this.resizeStage();
			window.onresize = () => {
				this.resizeStage();
				this.pixiApp.resize();

			}
		}
	}

	/**Transform screen space coordinate by camera transforms TODO: Rename ot getCameraCoordinate*/
	getScreenCoordinate(pos: vec2) {
		const cameraAdjX = (pos.x * this.sceneRoot.scale.x) + this.sceneRoot.x;
		const cameraAdjY = (pos.y * this.sceneRoot.scale.y) + this.sceneRoot.y;
		return new vec2([cameraAdjX, cameraAdjY]);
	}

	/** Stops pixi's shared ticker (particle effects, and any animated sprites or tweens), rendering has its own ticker */
	setPaused(paused: boolean) {
		const ticker = PIXI.Ticker.shared;
		if (paused) ticker.stop();
		else ticker.start();
	}

	async init() {
		await this.ready;
		this.deferredLighting = new DeferredRenderPass(this.depthBuffer, this.background);
		this.membranes = new MembraneFilter(this.membraneBuffer)
		this.depthOfField = new DepthOfField(this, this.depthBuffer, this.membraneBuffer);
		this.depthOfField.dirtMap = this.spriteFactory.lensDirtTexture;

		this.lighting.filters = [this.deferredLighting, this.membranes, this.depthOfField];
	}

	update(delta: number, time: number, cameraPosition: vec2, scaleFactor: number) {
		if (this.deferredLighting) { //filters not created before assets loaded.
			this.membranes.scaleFactor = scaleFactor;
			this.deferredLighting.scaleFactor = scaleFactor;
		}

		//Update all parallel "scene roots" to match up with camera
		this.inSceneUI.position.set(this.sceneRoot.position.x, this.sceneRoot.position.y);
		this.inSceneUI.scale.set(this.sceneRoot.scale.x, this.sceneRoot.scale.y);

		this.depthRoot.position.set(this.sceneRoot.position.x, this.sceneRoot.position.y);
		this.depthRoot.angle = this.sceneRoot.angle;
		this.depthRoot.scale.set(this.sceneRoot.scale.x, this.sceneRoot.scale.y);

		this.membraneRoot.position.set(this.sceneRoot.position.x, this.sceneRoot.position.y);
		this.membraneRoot.angle = this.sceneRoot.angle;
		this.membraneRoot.scale.set(this.sceneRoot.scale.x, this.sceneRoot.scale.y);

		//update background and cilia stuff
		this.background.update(delta, time, cameraPosition, scaleFactor);
		this.cilia.update(delta, time, cameraPosition, scaleFactor);

		//Render depth
		this.renderer.render(this.depthRoot, this.depthBuffer);
		this.depthBuffer.baseTexture.update(); //are these needed?

		//Add/Update noise in the membranes
		this.spriteFactory.updateDepthNoise(delta);

		//Render membrane depth
		this.renderer.render(this.membraneRoot, this.membraneBuffer);
		this.membraneBuffer.baseTexture.update();
	}

	resizeStage() {
		const wRatio = this.pixiApp.renderer.width / Resolution.Width;
		const hRatio = this.pixiApp.renderer.height / Resolution.Height;
		this.pixiApp.stage.scale.set(wRatio, hRatio);
		this.lighting.filterArea = new PIXI.Rectangle(0, 0, Resolution.Width * wRatio, Resolution.Height * hRatio);

		//Update the mouse source as well, so interaction events keep track
		const target = document.querySelector('.mousesource') as any;
		target.width = target.offsetWidth; //this seems needed cause pixi tries to read these?
		target.height = target.offsetHeight;
	}

	getMouseCoords() {
		const mousePoint = this.pixiApp.renderer.plugins.interaction.mouse.getLocalPosition(this.sceneRoot);
		return new vec2([mousePoint.x, mousePoint.y])
	}
}